import { useCallback, useMemo } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { animated, useTransition } from '@react-spring/web';
import AddIcon from '@/material-icons/400-24px/add.svg?react';

import { addReaction, removeReaction } from '../actions/interactions';
import { AnimatedNumber } from '../components/animated_number';
import { AnimateEmojiProvider } from '../components/emoji/context';
import { Icon } from '../components/icon';
import { ReactionEmoji } from '../components/reaction_emoji';
import EmojiPickerDropdown from '../features/compose/containers/emoji_picker_dropdown_container';
import { useAppDispatch } from '../store';

const messages = defineMessages({
  react: { id: 'status.react', defaultMessage: 'React' },
});

const reactionNameFromEmoji = emoji => {
  if (emoji.custom) {
    return emoji.id;
  }

  return emoji.native?.replaceAll(/:/g, '');
};

const Reaction = ({ statusId, reaction, style, disabled }) => {
  const dispatch = useAppDispatch();
  const handleClick = useCallback(() => {
    if (disabled && !reaction.get('me')) {
      return;
    }

    if (reaction.get('me')) {
      dispatch(removeReaction(statusId, reaction.get('name')));
    } else {
      dispatch(addReaction(statusId, reaction.get('name'), reaction.get('url')));
    }
  }, [disabled, dispatch, reaction, statusId]);

  return (
    <animated.button
      type='button'
      className={classNames('reactions-bar__item', { active: reaction.get('me') })}
      onClick={handleClick}
      style={style}
    >
      <AnimateEmojiProvider
        as='span'
        className='reactions-bar__item__emoji'
      >
        <ReactionEmoji
          name={reaction.get('name')}
          url={reaction.get('url')}
          staticUrl={reaction.get('static_url')}
        />
      </AnimateEmojiProvider>
      <span className='reactions-bar__item__count'>
        <AnimatedNumber value={reaction.get('count')} />
      </span>
    </animated.button>
  );
};

Reaction.propTypes = {
  statusId: PropTypes.string.isRequired,
  reaction: PropTypes.object.isRequired,
  style: PropTypes.object,
  disabled: PropTypes.bool,
};

const StatusReactions = ({ statusId, reactions, canReact }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const visibleReactions = useMemo(
    () => reactions.filter(reaction => reaction.get('count') > 0).toArray(),
    [reactions],
  );
  const selectedReactions = useMemo(
    () => reactions.filter(reaction => reaction.get('me')).count(),
    [reactions],
  );
  const disabled = selectedReactions >= 1;

  const handleEmojiPick = useCallback(emoji => {
    const name = reactionNameFromEmoji(emoji);

    if (name) {
      dispatch(addReaction(statusId, name, emoji.imageUrl));
    }
  }, [dispatch, statusId]);

  const transitions = useTransition(visibleReactions, {
    from: { scale: 0 },
    enter: { scale: 1 },
    leave: { scale: 0 },
    keys: visibleReactions.map(reaction => reaction.get('name')),
  });

  if (visibleReactions.length === 0 && !canReact) {
    return null;
  }

  return (
    <div className={classNames('reactions-bar', { 'reactions-bar--empty': visibleReactions.length === 0 })}>
      {transitions(({ scale }, reaction) => (
        <Reaction
          key={reaction.get('name')}
          reaction={reaction}
          statusId={statusId}
          disabled={disabled}
          style={{ transform: scale.to(s => `scale(${s})`) }}
        />
      ))}

      {canReact && (
        <EmojiPickerDropdown
          disabled={disabled}
          title={intl.formatMessage(messages.react)}
          iconComponent={AddIcon}
          onPickEmoji={handleEmojiPick}
        />
      )}
    </div>
  );
};

StatusReactions.propTypes = {
  statusId: PropTypes.string.isRequired,
  reactions: PropTypes.object.isRequired,
  canReact: PropTypes.bool,
};

export default StatusReactions;
